<template>
  <div class="container">
    <BLoading :active.sync="isLoading" />
    <div
      v-if="isLtrs"
      class="ls-service-details--back-to-my-services-wrapper"
      @click="$router.go(-1)"
    >
      <LsIcon
        class="ls-service-details--back-to-my-services-content ls-service-details--chevron"
        type="chevron"
        color="primary"
        size="lg"
      />
      <span
        test-id="ls-service-details--my-scheduled-services"
        class="ls-service-details--back-to-my-services-content"
        >{{ t('myScheduledServices') }}</span
      >
    </div>
    <div v-if="!isLoading" class="columns">
      <div class="column is-8">
        <div class="ls-service-details--header">
          <div>
            <h1
              class="ls-service-details--schedule-header"
              :class="{ 'ls-service-details--service-header': isLtrs }"
            >
              {{ details.scheduleServiceName }}
            </h1>
            <span
              v-if="isLtrs"
              test-id="ls-service-details--frequency"
              class="ls-service-details--frequency"
              >{{ t('lawnTreatment.recurring') }}</span
            >
            <span v-if="!isLtrs" test-id="service-frequency">{{ details.frequency }}</span> <br />
            <span v-if="details.scope">{{ details.scope }}</span
            ><br v-if="details.scope" />
            <div v-if="isLtrs" class="ls-service-details--price-wrapper">
              <strong>{{ details.price | currency }}</strong> <span> {{ t('perService') }}</span
              ><br />
            </div>
            <div v-if="!isLtrs">
              <strong>{{ details.price | currency }}</strong
              ><span> {{ t('perService') }}</span
              ><br />
            </div>
            <strong
              class="ls-service-details--header-bottom"
              :class="{ 'ls-service-details--header-bottom-address': isLtrs }"
              >{{ details.street }}</strong
            >
            <br />
            <span
              v-if="!isLtrs"
              test-id="service-status"
              class="ls-service-details--header-bottom"
              >{{ details.status }}</span
            >
          </div>
          <LsButton
            v-if="serviceHistory.schedule.quote_schedule_id"
            track-id="service_details_view-quote"
            bg-color="primary-light"
            text-color="primary"
            :text="`quote for ${details.scheduleServiceName}`"
            @click="
              $router.push({
                name: 'quoteDetails',
                params: {
                  property_id,
                  schedule_id: serviceHistory.schedule.quote_schedule_id,
                },
              })
            "
          />
        </div>
        <LsServiceDetailsCard
          v-if="isLtrs"
          :is-ltrs="isLtrs"
          :ltrs-grouped-by-schedule="ltrsGroupedBySchedule"
          :is-service-window-confirmed="isServiceWindowConfirmed"
          :service-type="serviceTypes.UPCOMING_SERVICE"
          :long-grass-params="longGrassReportParams"
          :schedule-event="upcomingScheduleEvent"
        />
        <LsServiceDetailsCard
          v-if="upcomingScheduleEvent && !isLtrs"
          :service-type="serviceTypes.UPCOMING_SERVICE"
          :long-grass-params="longGrassReportParams"
          :schedule-event="upcomingScheduleEvent"
        />

        <LsServiceDetailsCard
          v-if="!isLtrs"
          :service-type="serviceTypes.PREVIOUS_SERVICE"
          :long-grass-params="longGrassReportParams"
          :schedule-event="previousScheduleEvent.scheduleEvent"
          test-id="service_details_view-previous_service"
        />

        <div
          v-if="serviceHistory.schedule.property.can_request_additional_services && !isLtrs"
          class="ls-service-details--order-card"
        >
          <span class="ls-service-details--order-card-header">
            Do you need a one-time cleanup, leaf removal, shrub care, or anything else?
          </span>
          <LsButton
            track-id="service_details_view-request_quote"
            test-id="request-quote"
            text="Request a quote"
            @click="
              $router.push({
                name: 'orderService',
                params: { property_id },
              })
            "
          />
        </div>
        <div v-if="isLtrs && whatIsIncluded.answer" class="ls-service-details--question-wrapper">
          <span
            test-id="ls-service-details--what-is-included-question"
            class="ls-service-details--what-is-included-question"
          >
            {{ whatIsIncluded.question }}
          </span>
          <p
            test-id="ls-service-details--what-is-included-answer"
            class="ls-service-details--what-is-included-answer"
          >
            {{ whatIsIncluded.answer }}
          </p>
        </div>
        <div class="ls-service-details--links">
          <div v-if="!isLtrs">
            <span class="ls-service-details--sub-heading">Additional Service Options</span>
            <LsNavItem
              v-for="{ action, title } in options"
              :key="action"
              :title="title"
              @click.native="onClick(action)"
            />
            <LsNavItem
              title="Service history"
              @click.native="
                $router.push({
                  name: 'serviceHistory',
                  params: {
                    property_id,
                    schedule_id,
                  },
                })
              "
            />
          </div>
          <div v-if="details.isMowing">
            <span class="ls-service-details--sub-heading">FAQ</span>
            <LsNavItem
              v-for="{ action, title } in faqs"
              :key="action"
              :title="title"
              @click.native="onClick(action)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapState, mapActions, mapGetters } from 'vuex';
import {
  LTRS__GET_SCHEDULE_EVENTS,
  LTRS__GROUPED_BY_SCHEDULE,
  MY_SERVICES__PROPERTIES_WITH_GROUPED_SERVICES,
  SERVICE_HISTORY__GET_SCHEDULE,
} from '@/store/types';
import {
  lsFilters,
  lsRouterHelpers,
  lsScheduleHelpers,
  lsDateHelpers,
  lsModalService,
  lsErrorService,
  lsPropertyHelpers,
} from 'ls-vue-common';
import { LsButton, LsIcon } from '@/components/common';
import { lsScheduleEventServiceItemHelpers } from '@/components/ServiceItems/LsScheduleEventServiceItem/LsScheduleEventServiceItemHelpers';
import { isServiceWindowConfirmed } from '@/helpers/ltrs';
import { getLookupByActionName, openLongGrassFee } from '@/store/modules/support/support.helpers';
import { ChangeFrequencyModal } from '@/modals';
import { LONG_GRASS_TYPE, SERVICE_TYPES } from '@/constants';
import LsServiceDetailsCard from '@/views/Authorized/ServicesView/ServiceDetailsView/components/LsServiceDetailsCard.vue';
import LsNavItem from './components/LsNavItem.vue';

const options = [
  { title: 'Message my crew', action: 'message_crew' },
  {
    title: 'Skip next mow',
    action: 'skip',
  },
  {
    title: 'Change mowing frequency',
    action: 'change_frequency',
  },
  {
    title: 'Pause service',
    action: 'pause',
  },
];

const faqs = [
  {
    title: 'What does lawn mowing include?',
    action: 'included_with_mowing',
  },
  {
    title: 'What is long grass fee?',
    action: 'long_grass_fee',
  },
];

export default {
  name: 'ServiceDetailsView',
  components: {
    LsIcon,
    LsServiceDetailsCard,
    LsButton,
    LsNavItem,
  },
  data() {
    return {
      property_id: lsRouterHelpers.getParam(this.$route, 'property_id'),
      schedule_id: lsRouterHelpers.getParam(this.$route, 'schedule_id'),
      isCheckingRouting: true,
      faqs,
    };
  },
  computed: {
    ...mapGetters([LTRS__GROUPED_BY_SCHEDULE, MY_SERVICES__PROPERTIES_WITH_GROUPED_SERVICES]),
    ...mapState(['ltrs', 'serviceHistory']),
    serviceTypes() {
      return SERVICE_TYPES;
    },
    isLtrs() {
      return _.get(this.serviceHistory.schedule, 'service.is_ltrs', false);
    },
    whatIsIncluded() {
      const serviceId = _.get(this.serviceHistory.schedule, 'service.str_id');
      return {
        question: this.t('lawnTreatment.whatIsIncludedQuestion'),
        answer: this.t(`lawnTreatment.whatIsIncludedAnswer.${_.camelCase(serviceId)}`),
      };
    },
    ltrsGroupedBySchedule() {
      return this[LTRS__GROUPED_BY_SCHEDULE];
    },
    isServiceWindowConfirmed() {
      const schedules = _.get(this[LTRS__GROUPED_BY_SCHEDULE], this.schedule_id);
      if (!schedules) {
        return false;
      }

      return isServiceWindowConfirmed(schedules);
    },
    isLoading() {
      return this.serviceHistory.loadingStatus.isLoading || this.isCheckingRouting;
    },
    options() {
      return this.details.isMowing ? options : [options[0]];
    },
    showUpcomingScheduleEvent() {
      return (
        _.get(this.serviceHistory.schedule, 'next_scheduleevent_id') &&
        ['scheduled', 'pending'].includes(_.get(this.serviceHistory.schedule, 'next_eventstatus'))
      );
    },
    upcomingScheduleEvent() {
      const { type, isPending } = this.longGrassReportParams;
      if (!_.isEmpty(this.longGrassReportParams) && type === LONG_GRASS_TYPE.LGQ && isPending)
        return {};

      return this.showUpcomingScheduleEvent
        ? _.last(this.serviceHistory.schedule.scheduleevents)
        : null;
    },
    upcomingLongGrassEvent() {
      const upcomingEvent = this.showUpcomingScheduleEvent
        ? _.last(this.serviceHistory.schedule.scheduleevents)
        : null;

      const upcomingDisplayValues = lsScheduleEventServiceItemHelpers.getScheduleEventServiceItemDisplayValues(
        {
          scheduleEvent: upcomingEvent,
        }
      );

      return {
        scheduleEvent: upcomingEvent,
        longGrass: _.get(upcomingDisplayValues, 'longGrass'),
      };
    },
    previousScheduleEvent() {
      const scheduleEvent = lsScheduleHelpers.getPreviousScheduleEvent({
        schedule: this.serviceHistory.schedule,
      });

      const displayValues = lsScheduleEventServiceItemHelpers.getScheduleEventServiceItemDisplayValues(
        {
          scheduleEvent,
        }
      );

      return {
        scheduleEvent,
        longGrass: _.get(displayValues, 'longGrass'),
      };
    },
    longGrassReportParams() {
      let selectedEvent = this.previousScheduleEvent;

      if (!this.previousScheduleEvent.longGrass && this.upcomingLongGrassEvent.longGrass)
        selectedEvent = this.upcomingLongGrassEvent;

      if (!selectedEvent.longGrass) return {};
      const { longGrass, scheduleEvent } = selectedEvent;
      const {
        type,
        id,
        is_pending,
        instant_quote,
        longgrass_scope_label,
        service_schedule_event_id,
      } = longGrass;

      let info = {};
      switch (type) {
        case LONG_GRASS_TYPE.LGQ:
          info = {
            screen: 'LongGrassQuoteReport',
            isPending: is_pending,
            reportId: instant_quote.id,
          };
          break;

        case LONG_GRASS_TYPE.LGF:
          info = {
            screen: 'LongGrassFeeReport',
            reportId: id,
            available: longgrass_scope_label !== null,
          };
          break;
      }

      return {
        type,
        screen: info.screen,
        isScopeAvailable: _.isBoolean(info.available) ? info.available : true,
        isPending: _.isBoolean(info.isPending) ? info.isPending : true,
        params: {
          scheduleevent_id: service_schedule_event_id || scheduleEvent.id,
          report_id: info.reportId,
        },
      };
    },
    lookupByActionName() {
      return getLookupByActionName(_.concat(options, faqs));
    },
    details() {
      const { schedule } = this.serviceHistory;
      const { property } = schedule;
      const isMowing = _.get(schedule, 'service.str_id') === 'mowing';

      const frequency =
        schedule.type === 'service' || schedule.type === 'instant_quote'
          ? lsFilters.frequency({ cycle: schedule.cycle })
          : null;

      const startStatus = `Started ${lsFilters.date({ date: schedule.created_at })}`;
      const scheduleStatus = lsScheduleHelpers.getScheduleStatus({ schedule });
      let endStatus = '';
      if (scheduleStatus === 'cancelled' || scheduleStatus === 'ended') {
        endStatus = `${_.capitalize(scheduleStatus)} ${lsFilters.date({
          date: schedule.status_set_at,
        })}`;
      }
      let status = startStatus;
      if (startStatus && endStatus) {
        status = `${startStatus} - ${endStatus}`;
      }

      return {
        isMowing,
        scheduleServiceName: lsScheduleHelpers.getScheduleServiceName({ schedule }),
        frequency,
        scope: isMowing ? lsPropertyHelpers.getScope(property) : null,
        price: schedule.price || _.get(schedule, 'instant_quote.price'),
        street: property.street,
        status,
      };
    },
  },
  async created() {
    await this[SERVICE_HISTORY__GET_SCHEDULE]({
      property_id: this.property_id,
      schedule_id: this.schedule_id,
    });

    if (_.isEmpty(this.serviceHistory.schedule)) {
      return this.$router.push({
        name: 'services',
      });
    }
    if (this.serviceHistory.schedule.type === 'manualquote') {
      return this.$router.push({
        name: 'quoteDetails',
      });
    }
    if (this.serviceHistory.schedule.type === 'instant_quote') {
      return this.$router.push({
        name: 'instantQuoteDetails',
      });
    }

    if (this.isLtrs && _.isEmpty(this[LTRS__GROUPED_BY_SCHEDULE])) {
      await this[LTRS__GET_SCHEDULE_EVENTS]();
    }

    this.isCheckingRouting = false;
  },
  methods: {
    ...mapActions([LTRS__GET_SCHEDULE_EVENTS, SERVICE_HISTORY__GET_SCHEDULE]),
    getDateText({ startDate, endDate }) {
      return lsDateHelpers.getDateRange({
        startDate,
        endDate,
      });
    },
    changeFrequency() {
      lsModalService.open({
        parent: this,
        component: ChangeFrequencyModal,
      });
    },
    onClick(actionName) {
      const { method, route } = this.lookupByActionName[actionName] || {};
      if (method) {
        this[method]();
      } else if (route) {
        this.$router.push({
          name: route,
          params: {
            property_id: this.$route.params.property_id,
          },
        });
      } else {
        lsErrorService.log(`No method or route was associated with the action ${actionName}`);
      }
    },
    openLongGrassFee() {
      openLongGrassFee();
    },
  },
};
</script>

<style lang="scss" scoped>
.ls-service-details--chevron {
  transform: rotate(180deg);
}

.ls-service-details--header-bottom-address {
  margin-top: 0.5rem;
  display: inline-block;
  font-size: $size-5;
}

.ls-service-details--service-header {
  margin-top: 2rem;
}

.ls-service-details--frequency {
  font-size: $size-4;
}

.ls-service-details--header {
  padding-bottom: 20px;
  font-size: $size-5;
  border-bottom: 1px solid $primary-light;
  display: flex;
  justify-content: space-between;
  color: $primary;

  strong {
    color: $primary;
  }

  .ls-service-details--schedule-header {
    font-weight: bold;
    margin-bottom: 10px;
  }

  .ls-service-details--frequency-text {
    margin-bottom: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid $primary-light;
  }

  .ls-service-details--header-bottom {
    color: $gray-primary;
  }

  .ls-service-details--price-wrapper {
    margin-top: 0.25rem;

    > strong {
      font-size: $size-1;
    }

    > span {
      margin-left: 1rem;
    }
  }
}

.ls-service-details--sub-heading,
.ls-service-details--what-is-included-question {
  font-size: $size-4;
  color: $primary;
  font-weight: bold;
}

.ls-service-details--card {
  margin-top: 20px;
  border-bottom: 1px solid $primary-light;

  > div {
    margin: 10px 0 20px 0;
    background: $primary-light;
    padding: 18px 18px 16px 18px;
    display: flex;
    > div {
      margin-top: 1px;
      margin-left: 10px;
      span {
        font-size: $size-5;
        color: $primary;
        &:nth-of-type(2) {
          font-weight: bold;
        }
      }
    }
  }
  .previous-service {
    align-items: center;
    display: flex;
    .long-grass-report {
      align-items: center;
      color: $theme-color-social-twitter;
      display: inline-flex;
      margin-left: auto;
      &:hover {
        color: $theme-color-social-twitter;
      }
    }
  }
}

.ls-service-details--order-card {
  display: flex;
  padding: 20px 0;
  border-bottom: 1px solid $primary-light;
  margin-bottom: 20px;

  .ls-service-details--order-card-header {
    color: $primary;
    font-size: $size-4;
    margin-right: 79px;
  }
}

.ls-service-details--back-to-my-services-wrapper {
  overflow: auto;
  white-space: nowrap;
  display: flex;
  cursor: pointer;

  > span {
    margin-left: 1rem;
  }
}

.ls-service-details--back-to-my-services-content {
  display: inline-block;
  font-size: $size-5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: $primary;

  strong {
    color: $primary;
  }
}

.ls-service-details--question-wrapper {
  margin-top: 1.5rem;
  width: 85%;
}

.ls-service-details--what-is-included-answer {
  margin-top: 1rem;
}

.ls-service-details--links {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;

  > div {
    width: 300px;
  }
}

@media (max-width: $mobile-max-width) {
  .ls-service-details--header {
    display: block;

    .ls-service-details--schedule-header {
      font-size: $size-3;
    }

    button {
      margin-top: 10px;
    }
  }

  .ls-service-details--links {
    display: block;
    > div {
      width: 100%;
      &:nth-of-type(2) {
        margin-top: 20px;
      }
    }
  }

  .ls-service-details--order-card {
    display: block;
    text-align: center;

    .ls-service-details--order-card-header {
      margin-right: 0px;
    }

    button {
      margin-top: 10px;
    }
  }
}
.themed-app {
  .ls-service-details--header {
    font-size: $theme-txt-body;
    border-bottom: $theme-color-primary-light;
    color: $theme-color-primary;
    strong {
      color: $theme-color-primary;
    }
    .ls-service-details--frequency-text {
      border-bottom: $theme-color-primary-light;
    }
    .ls-service-details--header-bottom {
      color: $theme-color-gray-darker;
    }
  }
  .ls-service-details--sub-heading {
    font-size: $theme-txt-body-lg;
    color: $theme-color-primary;
  }
  .ls-service-details--card {
    border-bottom: $theme-color-primary-light;
    > div {
      background: $theme-color-primary-light;
      > div {
        span {
          font-size: $theme-txt-body;
          color: $theme-color-primary;
        }
      }
    }
  }
  .ls-service-details--order-card {
    border-bottom: $theme-color-primary-light;
    .ls-service-details--order-card-header {
      color: $theme-color-primary;
      font-size: $theme-txt-body-lg;
    }
  }
  @media (max-width: $mobile-max-width) {
    .ls-service-details--header {
      .ls-service-details--schedule-header {
        font-size: $theme-txt-title-sm;
      }
    }
  }
}
</style>
